'use client';

import React from 'react';

type BaseProps = {
  children: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md';
  dark?: boolean;
  ariaLabel?: string;
};

type AnchorProps = BaseProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children' | 'aria-label'> & {
    href: string;
    onClick?: never;
  };

type ButtonProps = BaseProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children' | 'aria-label'> & {
    href?: never;
  };

type AnimatedButtonProps = AnchorProps | ButtonProps;

export const AnimatedButton: React.FC<AnimatedButtonProps> = ({
  children,
  className = '',
  size = 'md',
  dark = false,
  ariaLabel,
  ...props
}) => {
  const classes = [
    'animated-button',
    size === 'sm' ? 'animated-button--sm' : '',
    dark ? 'animated-button--dark' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = <span className="animated-button__text">{children}</span>;

  if ('href' in props && props.href) {
    const { href, ...anchorProps } = props;
    return (
      <a href={href} className={classes} aria-label={ariaLabel} {...anchorProps}>
        {content}
      </a>
    );
  }

  const { type, ...buttonProps } = props as ButtonProps;

  return (
    <button type={type ?? 'button'} className={classes} aria-label={ariaLabel} {...buttonProps}>
      {content}
    </button>
  );
};
